import { request } from './request';
import { DateRange } from 'react-day-picker';

export interface BankAccount {
  banking_id: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  is_default: boolean;
  created_at: string;
  stripe_account_id?: string;
  stripe_onboarding_completed?: boolean;
}

export interface PaymentHistoryItem {
  payment_id: string;
  amount: number;
  payment_amount: number;
  client_payment_date: string;
  admin_payment_date: string | null;
  payout_status: boolean;
  admin_approval: boolean | null;
  bid_project_id: string;
  bid_project: {
    bid: {
      job: {
        title: string;
        client_project_id: string;
      };
    };
  };
}

export interface ClientTransactionHistoryItem {
  payment_id: string;
  amount: number;
  type: 'payment' | 'refund' | 'fee';
  status: 'pending' | 'completed' | 'failed';
  date: string;
  job_title: string;
  project_id: string;
  description: string;
}

export interface ClientTransactionHistoryParams {
  userId: string;
  dateRange?: {
    from?: string;
    to?: string;
  };
  type?: 'payment' | 'refund' | 'fee';
  status?: 'pending' | 'completed' | 'failed';
  page?: number;
  limit?: number;
}

export interface PaginatedTransactionHistory {
  transactions: ClientTransactionHistoryItem[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

export interface PaymentDetails {
  payment_id: string;
  amount: number;
  admin_fee: number;
  payment_amount: number;
  client_payment_date: string;
  client_id: string;
  filmmaker_id: string;
  bid_project_id: string;
  date: Date;
}

export interface FeeStatistics {
  totalPayments: number;
  totalRevenue: number;
  totalApplicationFees: number;
  estimatedStripeFees: number;
  estimatedPlatformProfit: number;
  averageTransactionValue: number;
}

export const paymentsApi = {
  // Admin payment operations
  async getAdminPayments(dateRange?: DateRange) {
    const queryParams = new URLSearchParams();
    if (dateRange?.from) queryParams.append('from', dateRange.from.toISOString());
    if (dateRange?.to) queryParams.append('to', dateRange.to.toISOString());

    return request(`/admin/payments?${queryParams}`, {
      method: 'GET'
    });
  },

  // Backwards compatibility method for existing admin hooks
  async adminList(from?: string, to?: string) {
    const queryParams = new URLSearchParams();
    if (from) queryParams.append('from', from);
    if (to) queryParams.append('to', to);

    return request(`/admin/payments?${queryParams}`, {
      method: 'GET'
    });
  },

  // Payment details operations
  async getById(paymentId: string): Promise<PaymentDetails> {
    return request(`/payments/${paymentId}`, {
      method: 'GET'
    });
  },

  async getPaymentByJobId(jobId: string): Promise<PaymentDetails> {
    return request(`/payments/jobs/${jobId}/payment`, {
      method: 'GET'
    });
  },

  async checkPaymentExists(jobId: string): Promise<boolean> {
    const data = await request<{ exists: boolean }>(`/payments/jobs/${jobId}/payment/exists`, {
      method: 'GET'
    });
    return data.exists;
  },

  async getBidAmount(bidId: string): Promise<number> {
    const data = await request<{ bid_amount: number }>(`/payments/bids/${bidId}/amount`, {
      method: 'GET'
    });
    return data.bid_amount;
  },

  // Fee statistics
  async getFeeStatistics(dateRange?: DateRange): Promise<FeeStatistics> {
    const queryParams = new URLSearchParams();
    if (dateRange?.from) queryParams.append('from', dateRange.from.toISOString());
    if (dateRange?.to) queryParams.append('to', dateRange.to.toISOString());
    
    return request(`/payments/admin/fee-stats?${queryParams}`, {
      method: 'GET'
    });
  },

  // Filmmaker banking operations
  async getFilmmakerBanking(userId: string): Promise<BankAccount[]> {
    return request(`/payments/filmmaker/${userId}/banking`, {
      method: 'GET'
    });
  },

  async createBankAccount(userId: string, accountData: Omit<BankAccount, 'banking_id' | 'created_at'>): Promise<BankAccount> {
    return request(`/payments/filmmaker/${userId}/banking`, {
      method: 'POST',
      body: JSON.stringify(accountData)
    });
  },

  async updateBankAccount(userId: string, bankingId: string, accountData: Partial<BankAccount>): Promise<BankAccount> {
    return request(`/payments/filmmaker/${userId}/banking/${bankingId}`, {
      method: 'PATCH',
      body: JSON.stringify(accountData)
    });
  },

  async deleteBankAccount(userId: string, bankingId: string): Promise<void> {
    return request(`/payments/filmmaker/${userId}/banking/${bankingId}`, {
      method: 'DELETE'
    });
  },

  // Payment history operations
  async getFilmmakerPaymentHistory(userId: string): Promise<PaymentHistoryItem[]> {
    return request(`/payments/filmmaker/${userId}/payments`, {
      method: 'GET'
    });
  },

  // Client wallet operations
  async getClientWallet(userId: string): Promise<{ balance: number }> {
    return request(`/payments/client/${userId}/wallet`, {
      method: 'GET'
    });
  },

  // Client payment history
  async getClientTransactionHistory(params: ClientTransactionHistoryParams): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (params.dateRange?.from) queryParams.append('from', params.dateRange.from);
    if (params.dateRange?.to) queryParams.append('to', params.dateRange.to);
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return request(`/payments/client/${params.userId}/history?${queryParams}`, {
      method: 'GET'
    });
  },

  // Payment creation
  async createPayment(paymentData: {
    admin_fee: number;
    amount: number;
    bid_project_id: string;
    client_id: string;
    filmmaker_id: string;
    payment_amount: number;
    client_approval: boolean;
    client_payment_date: string;
    admin_approval: boolean;
    payout_status: boolean;
  }): Promise<{ payment_id: string }> {
    return request('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  // Comprehensive job payment creation that handles all complex logic
  async createJobPayment(params: {
    jobId: string;
    clientId: string;
  }): Promise<{ payment_id: string; amount: number }> {
    return request(`/payments/jobs/${params.jobId}/payment`, {
      method: 'POST',
      body: JSON.stringify({ clientId: params.clientId })
    });
  },

  // Payment approval operations
  async updatePaymentApproval(paymentId: string, approved: boolean = true): Promise<{ payment_id: string; admin_approval: boolean }> {
    return request(`/payments/${paymentId}/approval`, {
      method: 'PATCH',
      body: JSON.stringify({ admin_approval: approved })
    });
  },
}; 